import type { MessageEvent } from 'pubnub'
import { v4, validate } from 'uuid'
import { queryClient } from 'v2/configs/rq-setup'
import { getWhatsAppQueryKeys } from 'v2/pages/settings/whatsapp/templates/queries'
import { AdhocChannels, ConvChannels, type RChunk, type RTask } from 'v2/types/api/conversations'
import type { RWhatsappTemplate, RWhatsappTemplatesResponse } from 'v2/types/api/settings'
import {
  ChunkOrigin,
  type InputChunks,
  type OriginFetchMessages,
  RChunkTypes,
} from 'v2/types/chunk.external'
import {
  type Chunk,
  type ChunkMeta,
  ChunkSender,
  ChunkState,
  ChunkType,
  RChunkType,
} from 'v2/types/chunk.internal'
import { getUnknownChunk } from '.'
import {
  fromChunkApi,
  fromFetchMessages,
  fromPbMessage,
  isChatMessage,
  isFileMessage,
  isOriginFetchMessages,
  isRAiMessageChunk,
  isRChatButtonChunk,
  isRChatMenuButtonChunk,
  isRChatMultiProductChunk,
  isSignalMessage,
} from './typeguards'

export const getTemplate = (id: string) => {
  const response = queryClient.getQueryData<RWhatsappTemplatesResponse[]>(
    getWhatsAppQueryKeys().templates,
  )

  const template = response
    ?.flatMap((r) => r.templates_data.waba_templates)
    .find((t) => t.uuid === id)
  return template
}

// CHUNK_STEP_7 - update transformApiChunkToModel to use the new types (optional)
// Transformer for chunks fetched from the API
const transformApiChunkToModel = (
  m: RChunk,
  meta?: { templates: Record<string, RWhatsappTemplate> },
): Chunk<ChunkType> => {
  const isBot = m.from_user_name === 'bot' || m.from?.match('bot') || m.from_user?.match('bot')
  const isInbound = !(
    isBot ||
    (validate(m.from) && !String(m.from_user_name).toLowerCase().match('anonymous'))
  )
  // Human-agent chunks can arrive with from_user_name 'Anonymous' (name unresolved), which the
  // isInbound heuristic misreads as the customer. Instead, treat a valid-uuid `from` that differs
  // from the conversation's customer_contact as the agent, matching the widget's sender attribution.
  const isAgentByContact =
    !isBot && validate(m.from) && !!m.customer_contact && m.from !== m.customer_contact
  const sender =
    isBot || m.type === ChunkType.aiMessage
      ? ChunkSender.BOT
      : isAgentByContact
        ? ChunkSender.AGENT
        : isInbound
          ? ChunkSender.CUSTOMER
          : ChunkSender.AGENT
  const baseChunk = {
    id: m.message_id || m.uuid || m.id || v4(),
    timetoken: new Date(m.timestamp || Date.now()).getTime(),
    origin: ChunkOrigin.apiFetchChunks,
    sender,
    from: m.from_user_name || undefined,
    // Every chunk carries its conversation, the way the PubNub transformer does: media
    // downloads name it so the execution service signs with the conversation's own pair.
    contactUuid: m.contact_uuid as string,
  }

  if (m.type === RChunkType.chatMessage) {
    return {
      ...baseChunk,
      meta: {
        message: m.description as string,
        state: ChunkState.DELIVERED,
        stateReason: m.channel_meta.error_data?.comments || m.channel_meta.error_data?.description,
      } as ChunkMeta['message'],
      type: ChunkType.chatMessage,
      label: m.description,
      channel: m.channel,
    }
  }
  if (m.type === ChunkType.notes) {
    if (m.disposition) {
      return {
        ...baseChunk,
        meta: {
          description: m.description as string,
          disposition: m.disposition as string,
          subDisposition: m.sub_disposition as string,
        } as ChunkMeta['disposition'],
        type: ChunkType.disposition,
        channel: m.channel,
      }
    }
    return {
      ...baseChunk,
      meta: { message: m.description as string } as ChunkMeta['notes'],
      type: ChunkType.notes,
      channel: ConvChannels.NOTES,
    }
  }
  if (m.type === ChunkType.task) {
    const taskAttributes = m?.attributes?.length ? (m.attributes[0] as RTask) : {}
    return {
      ...baseChunk,
      meta: { ...taskAttributes } as ChunkMeta['task'],
      type: ChunkType.task,
      channel: m.channel,
    }
  }
  if (
    [ChunkType.chatButton, ChunkType.chatMultiProduct, ChunkType.chatMenuButton].includes(
      m.type as ChunkType,
    )
  ) {
    return {
      ...baseChunk,
      meta: {
        message: m.attributes,
      },
      type: m.type as ChunkType,
      channel: m.channel,
    }
  }

  if (m.type === ChunkType.multiProductReply) {
    const resp = {
      ...baseChunk,
      meta: { message: m.attributes?.[0] },
      type: m.type as ChunkType,
      channel: m.channel,
    }
    return resp
  }

  // file and sticker are the same
  if (m.type === ChunkType.file || m.type === RChunkTypes.sticker) {
    return {
      ...baseChunk,
      meta: {
        fileData: m.attributes,
        senderId: m.sender_id,
        message: m.description,
        state: m.channel_meta.status,
        stateReason: m.channel_meta.error_data?.comments || m.channel_meta.error_data?.description, // This is the reason for the state
      },
      type: m.type as ChunkType.file,
      channel: m.channel,
      label: 'File message',
    }
  }

  if (m.type === ChunkType.aiMessage) {
    return {
      ...baseChunk,
      meta: { attributes: m.attributes },
      type: m.type as ChunkType,
      label: m.description,
      channel: m.channel,
    }
  }

  if (m.type === ChunkType.signal) {
    return {
      ...baseChunk,
      meta: {
        message: m.description,
        attributes: m.attributes,
        transferType: m.channel_meta.transfer_type,
      } as ChunkMeta[ChunkType.signal],
      label: m.description,
      type: m.type as ChunkType,
      channel: m.channel,
    }
  }

  if (m.type === ChunkType.flowExecution) {
    return {
      ...baseChunk,
      meta: m.attributes?.[0] as never,
      type: ChunkType.flowExecution,
      channel: m.channel,
    }
  }

  if (m.type === ChunkType.text) {
    return {
      ...baseChunk,
      meta: {
        message: m.description,
        state: m.channel_meta.status,
        stateReason: m.channel_meta.error_data?.comments || m.channel_meta.error_data?.description, // This is the reason for the state
        channel_meta: m.channel_meta,
        repliedMessage: m.channel_meta.replied_message?.description,
      } as ChunkMeta[ChunkType.text],
      type: ChunkType.text,
      channel: m.channel,
      label: m.description,
    }
  }

  if (m.type === ChunkType.internal_chat) {
    return {
      ...baseChunk,
      meta: {
        message: m.description,
        from_uuid: m.from,
        from_name: m.from_user_name,
      },
      type: ChunkType.internal_chat,
      channel: AdhocChannels.PRIVATE_CHAT,
    }
  }

  if (m.type === ChunkType.template) {
    const templateId = (
      m.attributes?.[0] as {
        template_uuid: string
      }
    )?.template_uuid
    return {
      ...baseChunk,
      meta: {
        templateData: m.attributes?.[0],
        template: meta?.templates[templateId] || getTemplate(templateId),
        state: m.channel_meta.status,
        stateReason: m.channel_meta.error_data?.comments,
        channel_meta: m.channel_meta,
      } as ChunkMeta[ChunkType.template],
      type: ChunkType.template,
      channel: m.channel,
      label: 'Template message sent',
    }
  }

  if (m.type === ChunkType.recording || m.type === ChunkType.voicemail) {
    return {
      ...baseChunk,
      meta: {
        recordings: m.attributes,
        isInbound: m.is_inbound_chunk,
      } as ChunkMeta['recording'],
      type: m.type,
      channel: ConvChannels.CALL,
    }
  }

  if (m.type === ChunkType.button) {
    const templateId = (
      m.channel_meta.replied_message?.attributes?.[0] as {
        template_uuid: string
      }
    )?.template_uuid

    return {
      ...baseChunk,
      channel: m.channel,
      type: ChunkType.button,
      label: m.description,
      meta: {
        templateData: m.channel_meta.replied_message?.attributes?.[0],
        template: meta?.templates[templateId] || getTemplate(templateId),
        originalMessage: templateId || m.channel_meta.replied_message?.description,
        replyMessage: m.description,
        message: m.attributes,
      } as ChunkMeta[ChunkType.button],
    }
  }

  if (m.type === ChunkType.button_reply) {
    return {
      ...baseChunk,
      meta: {
        message: m.attributes?.[0],
        state: m.channel_meta.status,
        stateReason: m.channel_meta.error_data?.comments || m.channel_meta.error_data?.description,
        channel_meta: m.channel_meta,
        repliedMessage: m.channel_meta.replied_message?.description,
      } as ChunkMeta[ChunkType.text],
      type: ChunkType.button_reply,
      channel: m.channel,
      label: m.description,
    }
  }

  if (m.type === ChunkType.list) {
    const listMeta = m.attributes?.[0] as ChunkMeta[ChunkType.list]
    return {
      ...baseChunk,
      meta: listMeta,
      type: ChunkType.list,
      channel: m.channel,
      label: listMeta?.header || listMeta?.body || '',
    }
  }

  if (m.type === ChunkType.list_reply) {
    const listReplyMeta = m.attributes?.[0] as ChunkMeta[ChunkType.list_reply]
    return {
      ...baseChunk,
      meta: listReplyMeta,
      type: ChunkType.list_reply,
      channel: m.channel,
      label: listReplyMeta?.title || '',
    }
  }

  return getUnknownChunk(m) as Chunk<ChunkType.unknown>
}

// CHUNK_STEP_6 - update transformPbMessage to use the new types
// Transformer for chunks fetched from PubNub
const transformPbMessage = (m: MessageEvent | OriginFetchMessages): Chunk<ChunkType> => {
  const baseChunk = isOriginFetchMessages(m)
    ? {
        id: m.pbMessage.timetoken as string,
        contactUuid: m.conversation?.contact_uuid as string,
        conversationNumber: Number(m.conversation?.conversation_number),
        channel: ConvChannels.CHAT, // Confirm if this is correct
        timetoken: Number(m.pbMessage.timetoken),
        origin: ChunkOrigin.pubnubFetchMessages,
      }
    : {
        id: m.timetoken,
        contactUuid: m.channel,
        channel: ConvChannels.CHAT, // Confirm if this is correct
        timetoken: Number(m.timetoken),
        origin: ChunkOrigin.pubnubChatEvent,
      }

  const isFetchedFromHistory = isOriginFetchMessages(m)
  const message = isFetchedFromHistory ? m.pbMessage.message : m.message

  if (isFileMessage(message)) {
    return {
      ...baseChunk,
      id: message.tmpId || v4(),
      meta: {
        sender: message.content.sender,
        fileData: message.content.message,
        // A human-leg image+caption is ONE file message carrying the caption in content.caption;
        // surface it as meta.message so the live bubble renders it (the history transform already
        // does this via m.description).
        message: message.content.caption,
        state: isFetchedFromHistory ? ChunkState.DELIVERED : ChunkState.SENT,
      } as ChunkMeta[ChunkType.file],
      type: ChunkType.file,
      label: message.content.message?.[0]?.file_name || 'File',
      sender: message.content.sender as ChunkSender,
    }
  }

  if (isChatMessage(message)) {
    return {
      ...baseChunk,
      id: message.tmpId || v4(),
      meta: {
        message: message.content.message,
        sender: message.content.sender,
        state: isFetchedFromHistory ? ChunkState.DELIVERED : ChunkState.SENT,
      } as ChunkMeta[ChunkType.chatMessage],
      type: ChunkType.chatMessage,
      label: message.content.message,
      sender: message.content.sender as ChunkSender,
    }
  }

  if (isSignalMessage(message)) {
    return {
      ...baseChunk,
      meta: {
        eventName: message.content.event_name,
        message: message.content.message,
        botUuid: message.content.bot_uuid,
      } as ChunkMeta[ChunkType.signal],
      type: ChunkType.signal,
      label: message.content.event_name,
      sender: ChunkSender.SYSTEM,
    }
  }

  if (isRAiMessageChunk(message)) {
    return {
      ...baseChunk,
      meta: message.content,
      type: ChunkType.aiMessage,
      label: message.content.description,
      sender: ChunkSender.BOT,
    }
  }

  if (isRChatButtonChunk(message)) {
    return {
      ...baseChunk,
      meta: message.content,
      type: ChunkType.chatButton,
      label: 'Chat Button',
      sender: ChunkSender.BOT,
    }
  }

  if (isRChatMultiProductChunk(message)) {
    return {
      ...baseChunk,
      meta: message.content,
      type: ChunkType.chatMultiProduct,
      label: 'Multi Product Carousel',
      sender: ChunkSender.BOT,
    }
  }

  if (isRChatMenuButtonChunk(message)) {
    return {
      ...baseChunk,
      meta: message.content,
      type: ChunkType.chatMenuButton,
      label: 'Menu Button',
      sender: ChunkSender.BOT,
    }
  }

  return getUnknownChunk({
    timestamp: baseChunk.timetoken,
    ...message,
  } as unknown as RChunk) as Chunk<ChunkType>
}

// CHUNK_STEP_5 - update toChunkModel to use the new types
// Base transformer for chunks fetched from any source
export const toChunkModel = (
  m: InputChunks,
  origin: ChunkOrigin,
  meta?: { templates: Record<string, RWhatsappTemplate> },
): Chunk<ChunkType> => {
  if (fromPbMessage(m, origin) || fromFetchMessages(m, origin)) return transformPbMessage(m)

  if (fromChunkApi(m, origin)) {
    return transformApiChunkToModel(m, meta)
  }

  return getUnknownChunk(m as RChunk) as Chunk<ChunkType.unknown>
}
